/**
 * Compatibility shim: re-implements the small slice of the `remix-validated-form`
 * API that this codebase uses, on top of `react-hook-form`. The two
 * `remix-validated-form` package specifiers are aliased to this module (see
 * vite.config.ts + tsconfig paths), so the ~18 form components and ~22 screens
 * keep their existing imports unchanged.
 *
 * Surface implemented (from a codebase audit):
 *   useField        -> { error, getInputProps, defaultValue, validate }
 *   useControlField -> [value, setValue]
 *   useHiddenField  -> registers a value the form should submit (see note)
 *   useFormContext  -> { hasBeenSubmitted, fieldErrors, isSubmitting }
 *   ValidatedForm   -> RHF form wrapper (zodResolver), calls `onSubmit(data)`
 *   validationError -> no-op (was a server-action helper; SPA has no actions)
 *
 * NOTE: the old `ValidatedForm` submitted to a Remix action/fetcher. In the SPA
 * each form screen supplies an `onSubmit` that calls an API mutation instead;
 * the legacy `method`/`action`/`fetcher`/`encType` props are accepted and
 * ignored for source-compatibility.
 */
import {
    FormProvider,
    useForm,
    useFormContext as useRHFFormContext,
    useController,
    type FieldValues,
    type SubmitHandler,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ZodTypeAny } from 'zod';
import { useEffect, type ReactNode, type FormEvent } from 'react';

export type Validator<T extends ZodTypeAny = ZodTypeAny> = { schema: T };

/** Permissive form-field props type (was exported by remix-validated-form). */
export type FieldProps = {
    name: string;
    label?: string;
    [key: string]: unknown;
};

// NOTE on null-safety: remix-validated-form tolerated its field hooks being
// used outside a form (global form store → safe defaults). Components like the
// Slider are reused read-only on non-form screens (e.g. the profiler summary),
// so the shim must not throw when there is no FormProvider. RHF's
// useFormContext() returns null in that case. Whether a component is inside a
// provider is stable for its lifetime, so the conditional hook in
// useControlField is safe.

// The 2nd argument (options / formId) is accepted for source-compatibility with
// remix-validated-form and ignored — the SPA only uses a single form per page.
export function useField(name: string, _options?: unknown) {
    const ctx = useRHFFormContext();

    if (!ctx) {
        return {
            error: undefined as string | undefined,
            getInputProps: (props: Record<string, unknown> = {}): Record<string, any> => ({
                name,
                ...props,
            }),
            defaultValue: undefined,
            validate: async () => ({ error: undefined }),
        };
    }

    const { register, getFieldState, formState, trigger } = ctx;
    const state = getFieldState(name, formState);
    const defaultValues = formState.defaultValues as
        | Record<string, unknown>
        | undefined;

    return {
        error: state.error?.message as string | undefined,
        getInputProps: (props: Record<string, unknown> = {}): Record<string, any> => ({
            ...register(name),
            ...props,
        }),
        defaultValue: defaultValues?.[name],
        validate: () => trigger(name),
    };
}

export function useControlField<T = any>(
    name: string,
    _formId?: string
): [T, (value: T) => void] {
    const ctx = useRHFFormContext();
    if (!ctx) {
        return [undefined as T, () => {}];
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { field } = useController({ name, control: ctx.control });
    return [field.value as T, (value: T) => field.onChange(value)];
}

/**
 * Carries a value into the submitted data without a visible input — the
 * replacement for `<input type="hidden" name={name} value={value} />`.
 *
 * remix-validated-form read the DOM on submit, so a plain hidden input was
 * enough. react-hook-form submits only registered fields (plus defaultValues),
 * so an unregistered hidden input is silently dropped from `onSubmit(data)`.
 * This registers `name` and keeps it equal to `value` as it changes.
 */
export function useHiddenField<T>(name: string, value: T): void {
    const [, setValue] = useControlField<T>(name);
    useEffect(() => {
        setValue(value);
        // `setValue` is a fresh closure on every render; the field identity
        // is what matters and that is fixed by `name`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, value]);
}

export function useFormContext(_formId?: string) {
    const ctx = useRHFFormContext();
    if (!ctx) {
        return {
            hasBeenSubmitted: false,
            isSubmitting: false,
            isValid: true,
            fieldErrors: {} as Record<string, string>,
            validate: async () => ({ error: undefined }),
        };
    }

    const { formState, trigger } = ctx;
    const fieldErrors = Object.fromEntries(
        Object.entries(formState.errors).map(([key, value]) => [
            key,
            (value as { message?: string } | undefined)?.message ?? '',
        ])
    );
    return {
        hasBeenSubmitted: formState.isSubmitted,
        isSubmitting: formState.isSubmitting,
        isValid: formState.isValid,
        fieldErrors,
        // remix-validated-form's validate() returned { error? }; map to RHF trigger.
        validate: async () => {
            const ok = await trigger();
            return ok ? { error: undefined } : { error: 'invalid' };
        },
    };
}

export function useIsSubmitting(_formId?: string): boolean {
    const ctx = useRHFFormContext();
    return ctx?.formState.isSubmitting ?? false;
}

export function useIsValid(_formId?: string): boolean {
    const ctx = useRHFFormContext();
    return ctx?.formState.isValid ?? true;
}

/** Server-action helper in the original; unused in the SPA. */
export function validationError(error: unknown): never {
    throw new Error(
        'validationError() is not supported in the SPA — handle validation in onSubmit instead.'
    );
}

type ValidatedFormProps<T extends FieldValues = FieldValues> = {
    validator: Validator;
    defaultValues?: Partial<T>;
    onSubmit?: (data: T, event?: FormEvent<HTMLFormElement>) => void | Promise<void>;
    id?: string;
    className?: string;
    onReset?: () => void;
    resetAfterSubmit?: boolean;
    children: ReactNode;
    // Legacy Remix props — accepted and ignored.
    method?: string;
    action?: string;
    fetcher?: unknown;
    encType?: string;
    noValidate?: boolean;
};

export function ValidatedForm<T extends FieldValues = FieldValues>({
    validator,
    defaultValues,
    onSubmit,
    id,
    className,
    onReset,
    resetAfterSubmit,
    children,
}: ValidatedFormProps<T>) {
    const methods = useForm<T>({
        resolver: zodResolver(validator.schema as ZodTypeAny) as never,
        defaultValues: defaultValues as never,
    });

    const submitHandler: SubmitHandler<T> = async (data, event) => {
        await onSubmit?.(data, event as FormEvent<HTMLFormElement>);
        if (resetAfterSubmit) methods.reset();
    };

    return (
        <FormProvider {...methods}>
            <form
                id={id}
                className={className}
                onReset={onReset}
                onSubmit={methods.handleSubmit(submitHandler)}
                noValidate
            >
                {children}
            </form>
        </FormProvider>
    );
}
